import { america, parentOf, type IdentifierLinks, type Mission } from '@/content/missions'

/**
 * Government-wide portals for the required links that have one. An agency
 * supplies its own page for each link; until it does, these stand in, and a
 * link with neither shows as text rather than pointing somewhere else.
 */
const governmentWide: IdentifierLinks = {
  foia: 'https://www.foia.gov/',
  oig: 'https://www.oversight.gov/',
  performance: 'https://www.performance.gov/',
}

/** The agency identifier: parent agency and the links every agency site carries. */
export default function Identifier({ mission, links = {} }: { mission: Mission; links?: IdentifierLinks }) {
  const nation = mission.id === america.id
  const parent = parentOf(mission)
  const agency = nation
    ? 'the United States government'
    : parent
      ? `${mission.short}, part of the ${parent.name}`
      : `the ${mission.name}`
  const items: Array<[keyof IdentifierLinks, string]> = [
    ['about', `About ${nation ? 'the government' : mission.short}`],
    ['accessibility', 'Accessibility statement'],
    ['foia', 'FOIA requests'],
    ['noFear', 'No FEAR Act data'],
    ['oig', 'Office of the Inspector General'],
    ['performance', 'Performance reports'],
    ['privacy', 'Privacy policy'],
  ]
  const destinations: IdentifierLinks = { about: mission.url, ...governmentWide, ...mission.identifier, ...links }
  // The agency's seal leads the masthead, or its logo where it has no seal.
  const mark = mission.seal
    ? { file: mission.seal.file, alt: `${mission.name} seal` }
    : mission.logo
      ? { file: mission.logo.file, alt: `${mission.name} logo` }
      : null
  return (
    <section className="mds-identifier" aria-label="Agency identifier">
      <div className="mds-identifier__head">
        {mark && (
          <img
            className="mds-identifier__mark"
            src={`/${mark.file.replace(/^public\//, '')}`}
            alt={mark.alt}
            loading="lazy"
            decoding="async"
          />
        )}
        <p className="mds-identifier__masthead">
          <span className="mds-identifier__domain">{new URL(mission.url).hostname}</span>
          An official website of {agency}
        </p>
      </div>
      <nav aria-label="Important links">
        <ul className="mds-identifier__links">
          {items.map(([key, label]) => (
            <li key={key}>{destinations[key] ? <a href={destinations[key]}>{label}</a> : <span>{label}</span>}</li>
          ))}
        </ul>
      </nav>
      <p className="mds-identifier__usa">
        Looking for U.S. government information and services? <a href="https://www.usa.gov/">Visit USA.gov</a>
      </p>
    </section>
  )
}
